import { HttpErrorResponse } from '@angular/common/http';

import { environment } from 'src/environments/environment';

export interface ApiFailure {
  status: number;
  method?: string;
  url: string;
}

/** What a failed API request no caller caught is reported to Sentry as: status, method, route and the API's message. */
export class ApiRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiRequestError';
    // The stack would only show the error handler, the same for every failure, and Sentry groups by stack
    // when there is one. Without it, Sentry groups by this title, so each failing route is its own issue.
    this.stack = '';
  }
}

const MAX_MESSAGE_LENGTH = 200;
const VERSION_SEGMENT = /^v\d+$/;
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SIGN_IN_SEGMENT = /^(login|password-recovery)$/;
const EMAIL = /[^\s@'"]+@[^\s@'"]+\.[^\s@'"]+/g;
const URL_IN_TEXT = /https?:\/\/\S+/g;
const TOKEN = /(?=[\w.~+/=-]*\d)[\w.~+/=-]{16,}/g;
const NUMBER = /\+?\d[\d\s().:-]*\d|\d/g;

// The ErrorInterceptor hands callers its own object ({ code, message, status, err }, the API body, or the
// ProgressEvent of a request that got no response), which says nothing of the request it came from.
const failures = new WeakMap<object, ApiFailure>();

/** Remembers the request an error handed to callers came from, so its Sentry report can name it. */
export function recordApiFailure(rejection: unknown, failure: ApiFailure): void {
  if (rejection && typeof rejection === 'object' && !(rejection instanceof Error)) {
    failures.set(rejection, failure);
  }
}

/**
 * Extractor for `Sentry.createErrorHandler()`. A failed API request becomes an `ApiRequestError` titled like
 * `403 GET api/v1/customer/task-lists/:id/applied-jobs: <API message>`, instead of Sentry's "Handled unknown error"
 * for every plain object. Anything else goes to Sentry's own extractor, so real Errors are reported as before.
 */
export function sentryErrorExtractor(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
  const candidate = (error as { ngOriginalError?: unknown })?.ngOriginalError ?? error;
  const failure = candidate && typeof candidate === 'object' ? failures.get(candidate) : undefined;
  if (failure) {
    return apiRequestError(failure, candidate instanceof HttpErrorResponse ? apiBody(candidate) : candidate);
  }
  // Thrown without passing the ErrorInterceptor. Sentry's default would title it with the full URL.
  if (candidate instanceof HttpErrorResponse) {
    return apiRequestError({ status: candidate.status, url: candidate.url ?? '' }, apiBody(candidate));
  }
  return defaultExtractor(error);
}

function apiBody(response: HttpErrorResponse): unknown {
  return response.error?.error ?? response.error;
}

function apiRequestError(failure: ApiFailure, body: unknown): ApiRequestError {
  const request = [failure.status, failure.method, apiRoute(failure.url)].filter(part => part !== undefined).join(' ');
  const apiMessage = (body as { message?: unknown })?.message;
  const message = typeof apiMessage === 'string' && apiMessage.trim() ? `${request}: ${scrubMessage(apiMessage)}` : request;
  return new ApiRequestError(message);
}

/**
 * The request's path as one route for every record: no query string, no slash at either end, UUIDs as `:uuid`,
 * other ids (any segment with a digit, an `@`, or over 30 characters) as `:id`, and sign-in keys left out.
 * The API host is left out; another host (S3, Google) is kept in front.
 */
export function apiRoute(url: string): string {
  const apiHost = new URL(environment.api_url).host;
  let parsed: URL;
  try {
    parsed = new URL(url, environment.api_url);
  } catch {
    return ':url';
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  // The sign-in key is dropped, not replaced: `scrubSentryEvent` blanks whatever follows `login/`, which would
  // swallow a `:token` placeholder together with the `: ` that starts the API message.
  const path = segments
    .filter((_, index) => !(index > 0 && SIGN_IN_SEGMENT.test(segments[index - 1])))
    .map(segment => UUID_SEGMENT.test(segment) ? ':uuid' : isId(segment) ? ':id' : segment)
    .join('/');

  return parsed.host === apiHost ? path : `${parsed.host}/${path}`;
}

function isId(segment: string): boolean {
  return !VERSION_SEGMENT.test(segment) && (/\d/.test(segment) || segment.includes('@') || segment.length > 30);
}

/** The API's message without addresses, emails, keys or numbers, so the same failure always reads the same. */
export function scrubMessage(message: string): string {
  const scrubbed = message
    .replace(URL_IN_TEXT, ':url')
    .replace(EMAIL, ':email')
    .replace(TOKEN, ':token')
    .replace(NUMBER, ':number')
    .trim();
  return scrubbed.length > MAX_MESSAGE_LENGTH ? `${scrubbed.slice(0, MAX_MESSAGE_LENGTH)}…` : scrubbed;
}
